import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { FloatingInput, Button, RadioButton } from '@piplapps/pipl-ui';
import { getText } from '../../../Utils/TranslationUtils';
import styles from './LeadVettingFormStyles.module.less';
import { trackEvent } from '../utils';
import TermsCheckbox from '../../../Components/TermsCheckbox';
import Captcha from '../../../Components/Captcha';
import AnalyticsEventNames from '../../../Utils/AnalyticsHandler/AnalyticsEventNames';
import AnalyticsHandler from '../../../Utils/AnalyticsHandler/AnalyticsHandler';
import PageviewHandler from '../../../Utils/AnalyticsHandler/PageviewHandler';

const VettingStep3 = (props) => {
  const { isLoading, onSubmit } = props;
  const [showCaptcha, setShowCaptcha] = useState(!!props.fields.useCaseCode);

  const validate = (values) => {
    const errors = {};
    for (const value in values) {
      if (value !== 'otherDescription') {
        if (values[value] === '' || !values[value]) {
          errors[value] = getText('ERROR_REQUIRED_FIELD');
        }
      }
    }

    if (values.useCaseCode === 'OTHER' && (values.otherDescription === '' || !values.otherDescription)) {
      errors.useCaseCode = getText('ERROR_REQUIRED_FIELD');
    }

    if (Object.values(errors).length > 0) {
      props.removeCompleted(3);
    }

    if (document.cookie.indexOf('automation_tests') > -1) {
      if (errors.terms) {
        delete errors.terms;
      }

      if (errors.captcha) {
        delete errors.captcha;
      }
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      useCaseCode: null,
      useCaseText: null,
      otherDescription: null,
      captcha: false,
      terms: false,
    },
    validate,
    onSubmit: (values) => {
      props.completeStep(3);
      onSubmit(values);
      trackEvent('submitFullForm', '');
    },
  });

  const textLine = (text) => {
    return <div className={styles.text}>{gettext(text)}</div>;
  };
  const textBox = (
    <div className={styles.wideInput}>
      <FloatingInput
        size="wide"
        placeholder={gettext('Specify how do you expect to use Pipl')}
        id="otherDescription_id"
        name="otherDescription"
        testId="otherDescription-input"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.otherDescription ? formik.values.otherDescription : ''}
        forcedError={
          formik.errors.otherDescription && formik.touched.otherDescription && formik.values.useCaseCode === 'OTHER'
            ? formik.errors.otherDescription
            : null
        }
        error={formik.errors.otherDescription}
        disabled={formik.values.useCaseCode !== 'OTHER'}
        height={100}
      />
    </div>
  );
  const options = [
    {
      id: 'IDENTITY_VERIFICATION',
      label: 'Identity Verification',
      child: textLine('Quick fact-check for transactional applications that require manual review'),
    },
    {
      id: 'INVESTIGATION',
      label: 'Investigation',
      child: textLine('in-depth research into relationships, history, user accounts etc.'),
    },
    {
      id: 'CONTACT_ENRICHMENT',
      label: 'Contact Enrichment',
      child: textLine('Look up primary or alternate contact information'),
    },
    { id: 'OTHER', label: 'Other', child: formik.values.useCaseCode === 'OTHER' ? textBox : null },
  ];
  const codeMap = {
    IDENTITY_VERIFICATION: 'VE_FCRA',
    INVESTIGATION: 'IN_FRAUD',
    CONTACT_ENRICHMENT: 'EN_GENERAL',
    OTHER: 'OTHER',
  };

  const termsError = formik.errors.terms && formik.touched.terms ? formik.errors.terms : null;
  const captchaError = formik.errors.captcha && formik.touched.captcha ? formik.errors.captcha : null;

  return (
    <div>
      <div>
        <PageviewHandler path="step3/" />
        <div className={styles.headerWrapper}>
          <div className={styles.pageTitle}>
            <span>{gettext('Business Application')}</span>
          </div>
        </div>
        <div>
          <form onSubmit={formik.handleSubmit} id="vetting-step-3-form">
            <div className={styles.radio}>
              {options ? (
                <RadioButton
                  options={options}
                  onCheck={(v) => {
                    formik.setFieldValue('useCaseText', v);
                    formik.setFieldValue('useCaseCode', codeMap[v]);
                    formik.setFieldTouched('useCaseCode');
                    trackEvent('useCase', v);
                    setShowCaptcha(true);
                    if (v && v !== 'OTHER') {
                      props.updateFields({ useCaseText: v });
                      props.updateFields({ useCaseCode: codeMap[v] });
                    }
                  }}
                  name="useCaseCode"
                  error={formik.errors.useCaseCode && formik.touched.useCaseCode ? formik.errors.useCaseCode : null}
                  value={props.fields.useCaseText}
                />
              ) : null}
            </div>
            {showCaptcha ? (
              <Captcha
                error={captchaError}
                onChange={(value) => {
                  trackEvent('captcha', 'captcha');
                  formik.setFieldValue('captcha', value);
                }}
              />
            ) : null}
            <TermsCheckbox
              error={termsError}
              product={props.product}
              onChange={(e) => {
                const isOn = formik.values.terms;
                trackEvent('terms', e.target.value);
                formik.setFieldValue('terms', !isOn);
                props.updateFields({ terms: formik.values.terms });
              }}
            />
            <div className={styles.proceedButton}>
              <AnalyticsHandler eventName={AnalyticsEventNames.SUBMIT_STEP_3}>
                <Button
                  id="submit-button"
                  buttonClass="primary-green"
                  type="submit"
                  text={gettext('Request Free Trial')}
                  isLoading={isLoading}
                  loadingText={gettext('Saving')}
                  checked={formik.values.terms}
                />
              </AnalyticsHandler>
            </div>
          </form>
          <div className={styles.buffer}>
            <span />
          </div>
          <div className={styles.lowerText}>
            {gettext('You may be asked to provide additional details via web forms or email correspondence.')}
          </div>
        </div>
      </div>
    </div>
  );
};
VettingStep3.propTypes = {
  isLoading: PropTypes.bool,
  product: PropTypes.string,
  onSubmit: PropTypes.func,
  completeStep: PropTypes.func,
  removeCompleted: PropTypes.func,
  updateFields: PropTypes.func,
  fields: PropTypes.object,
};

export default VettingStep3;
